"use client";

import { SOCIAL_LINKS } from "@guru/shared/constants/social-links";
import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import { ArrowUpRight } from "lucide-react";
import type { CSSProperties } from "react";
import { SpotlightCard } from "@/components/marketing/spotlight-card";
import { ScrollReveal } from "@/components/public/scroll-reveal";

/**
 * Per-platform texture: a one-line reason to tap, the verb that fits the
 * platform, and the real brand colour the icon tile adopts on hover. Keyed by
 * the SOCIAL_LINKS name, so a new channel just needs an entry here.
 */
const SOCIAL_META: Record<string, { tagline: string; action: string; bg: string }> = {
  Facebook: { tagline: "News & community", action: "Follow", bg: "#1877f2" },
  YouTube: { tagline: "Demos & walkthroughs", action: "Subscribe", bg: "#ff0000" },
  Instagram: {
    tagline: "Behind the scenes",
    action: "Follow",
    bg: "linear-gradient(135deg,#feda75 0%,#fa7e1e 25%,#d62976 55%,#962fbf 78%,#4f5bd5 100%)",
  },
  WhatsApp: { tagline: "Talk to a human", action: "Message us", bg: "#25d366" },
};

const FALLBACK_META = { tagline: "Say hello", action: "Open", bg: "var(--brand-saffron)" };

export function SocialSection() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-secondary/30 py-16">
      <div className="dot-pattern absolute inset-0" aria-hidden="true" />
      {/* Soft warm glow for depth behind the heading */}
      <div
        className="pointer-events-none absolute left-1/2 top-4 h-56 w-[36rem] max-w-full -translate-x-1/2 rounded-full bg-brand/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <ScrollReveal threshold={0.3}>
          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold text-foreground sm:text-3xl">
              {t.homepage.social.title}
            </h2>
            <p className="mt-2 text-muted-foreground">{t.homepage.social.subtitle}</p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100} threshold={0.3}>
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4">
            {SOCIAL_LINKS.map((social) => {
              const meta = SOCIAL_META[social.name] ?? FALLBACK_META;
              return (
                <SpotlightCard
                  key={social.name}
                  className={cn(
                    "stagger-child group relative flex flex-col items-start overflow-hidden rounded-2xl",
                    "border border-border bg-card p-5",
                    "transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  )}
                >
                  <div
                    className={cn(
                      "flex size-12 items-center justify-center rounded-xl",
                      "bg-secondary text-muted-foreground transition-colors duration-300",
                      "group-hover:text-white group-hover:[background:var(--social-bg)]"
                    )}
                    style={{ "--social-bg": meta.bg } as CSSProperties}
                  >
                    <social.icon className="size-6" aria-hidden="true" />
                  </div>

                  <h3 className="mt-4 text-base font-semibold text-foreground">{social.name}</h3>
                  <p className="mt-0.5 text-sm text-muted-foreground">{meta.tagline}</p>

                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand-deep">
                    {meta.action}
                    <ArrowUpRight
                      className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      aria-hidden="true"
                    />
                  </span>

                  {/* Stretched link — the whole card is the target */}
                  <a
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "absolute inset-0 rounded-2xl",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/70"
                    )}
                  >
                    <span className="sr-only">
                      {social.name} — {meta.tagline}
                    </span>
                  </a>
                </SpotlightCard>
              );
            })}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
